import React, { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Progress } from '@/components/ui/progress';
import { Loader2, Sun, Bell, Lightbulb, Compass, PartyPopper, ClipboardList } from 'lucide-react';
import { toast } from 'sonner';
import api from '@/lib/api';
import { AriaConsentCard } from '@/components/aria/AriaConsentCard';
import { MoodPicker } from '@/components/aria/AriaCards';

const Section = ({ icon: Icon, title, children, testid }) => (
  <div className="rounded-2xl border bg-card p-4 space-y-3" data-testid={testid}>
    <p className="text-sm font-semibold flex items-center gap-2"><Icon className="h-4 w-4 text-violet-500" aria-hidden="true" />{title}</p>
    {children}
  </div>
);

export const AriaTodayTab = ({ settings, onSettings, onAsk }) => {
  const { t, i18n } = useTranslation();
  const [data, setData] = useState(null);
  const [sending, setSending] = useState(false);

  const load = useCallback(() => {
    api.get('/aria/today', { params: { lang: i18n.language } }).then((r) => setData(r.data)).catch(() => setData({ error: true }));
  }, [i18n.language]);
  useEffect(() => { load(); }, [load, settings?.consent]);

  const checkin = async (score) => {
    setSending(true);
    try { await api.post('/aria/checkin', { mood_score: score }); toast.success(t('aria.today.checkinDone')); load(); }
    catch (e) { toast.error(e.response?.data?.detail || t('aria.error')); } finally { setSending(false); }
  };

  if (!data) return <div className="flex justify-center py-16"><Loader2 className="h-6 w-6 animate-spin text-violet-500" /></div>;
  if (data.error) return <p className="text-sm text-muted-foreground py-8 text-center" data-testid="aria-today-error">{t('aria.error')}</p>;
  const j = data.journey;
  const celebrate = j && (j.medal_today || j.anniversary_years > 0);

  return (
    <div className="space-y-4 pb-6" data-testid="aria-today-tab">
      <div>
        <h1 className="text-2xl sm:text-3xl font-bold" data-testid="aria-greeting">
          {data.greeting}{data.personal && data.first_name ? `, ${data.first_name}` : ''}!
        </h1>
        {j && (
          <p className="text-base text-muted-foreground mt-1" data-testid="aria-clean-time">
            {t('aria.today.cleanTime', { y: j.ymd.years, m: j.ymd.months, d: j.ymd.days })}
          </p>
        )}
        {data.personal && data.tone && <p className="text-sm text-violet-600 dark:text-violet-300 mt-1" data-testid="aria-tone">{t(`aria.tone.${data.tone}`)}</p>}
      </div>

      {!data.personal && settings && !settings.consent && <AriaConsentCard settings={settings} onChange={onSettings} />}

      {celebrate && (
        <Link to="/dashboard" className="flex items-center gap-3 rounded-2xl bg-gradient-to-r from-amber-500/20 to-violet-500/20 border border-amber-400/40 p-4" data-testid="aria-celebration">
          <PartyPopper className="h-7 w-7 text-amber-500" />
          <span className="text-sm font-semibold">{j.anniversary_years > 0 ? t('aria.today.anniversary', { n: j.anniversary_years }) : t('aria.today.medalDay', { medal: t(`medals.${j.current_medal_key}`, j.current_medal) })}</span>
        </Link>
      )}

      {data.profile_invite && (
        <Link to="/quiz/recuperacao" className="flex items-center gap-3 rounded-2xl border border-dashed p-4 text-sm" data-testid="aria-profile-invite">
          <ClipboardList className="h-5 w-5 text-green-500" />{t('aria.today.profileInvite')}
        </Link>
      )}

      {data.personal && (
        <Section icon={Sun} title={t('aria.today.howAreYou')} testid="aria-today-checkin">
          {data.checked_in_today
            ? <p className="text-sm text-muted-foreground" data-testid="aria-checkin-done">{t('aria.today.alreadyChecked')}</p>
            : <MoodPicker onPick={checkin} disabled={sending} testid="aria-today-mood" />}
          {data.last_checkin && <p className="text-xs text-muted-foreground">{t('aria.today.lastCheckin', { date: data.last_checkin.date, mood: data.last_checkin.mood_score })}</p>}
        </Section>
      )}

      {j && (
        <Section icon={PartyPopper} title={t('aria.today.nextMedal')} testid="aria-today-medal">
          <p className="text-sm">{j.next_medal ? t('aria.today.toNext', { medal: t(`medals.${j.next_medal_key}`, j.next_medal), n: j.days_to_next }) : t('aria.today.allMedals')}</p>
          <Progress value={j.progress} aria-label={t('aria.today.nextMedal')} />
          <p className="text-xs text-muted-foreground">{t('aria.today.currentMedal', { medal: t(`medals.${j.current_medal_key}`, j.current_medal) })}</p>
        </Section>
      )}

      <Section icon={Lightbulb} title={t('aria.today.reflection')} testid="aria-today-reflection">
        <p className="text-sm italic">“{data.reflection}”</p>
      </Section>

      <Section icon={Compass} title={t('aria.today.suggestion')} testid="aria-today-suggestion">
        <Link to={data.suggestion.path} className="text-sm text-violet-600 dark:text-violet-300 underline-offset-4 hover:underline">{t(`aria.suggest.${data.suggestion.kind}`, data.suggestion.title)}</Link>
      </Section>

      {data.personal && (
        <Section icon={Bell} title={t('aria.today.reminders')} testid="aria-today-reminders">
          {(data.reminders || []).length === 0 && <p className="text-sm text-muted-foreground">{t('aria.today.noReminders')}</p>}
          {(data.reminders || []).map((r) => (
            <p key={r.id} className="text-sm flex justify-between gap-2"><span className="break-words">{r.text}</span><span className="text-xs text-muted-foreground shrink-0">{new Date(r.remind_at).toLocaleString()}</span></p>
          ))}
          {(data.aria_messages || []).map((m, i) => <p key={i} className="text-xs rounded-lg bg-violet-50 dark:bg-violet-950/30 p-2">{m.message}</p>)}
        </Section>
      )}

      <div className="flex flex-wrap gap-2">
        {['askToday', 'askMedal', 'askTalk'].map((k) => (
          <button key={k} type="button" onClick={() => onAsk(t(`aria.quick.${k}`))} className="text-xs px-3 min-h-[40px] rounded-full border border-violet-200 dark:border-violet-800 text-violet-700 dark:text-violet-300 bg-violet-50 dark:bg-violet-950/30" data-testid={`aria-quick-${k}`}>
            {t(`aria.quick.${k}`)}
          </button>
        ))}
      </div>
      <p className="text-[11px] text-muted-foreground">{t('aria.disclaimer')}</p>
    </div>
  );
};
